import {
	Box,
	Button,
	CardActions,
	CardContent,
	FormHelperText,
	Grid,
	IconButton,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { API_DATE_TIME_FORMAT, isTime } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import AppColorTextField from '@/common/components/form-inputs/AppColorTextField';
import { GameColorGallerySeriesDTORequest } from '@/api/DTO/treasury/gallery.interface';
import {
	useCreateGalleryMutation,
	useUpdateGalleryMutation,
} from '@/api/treasury/galleryApiSlice';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { FileInfo } from '@/api/DTO/common.interface';
import { useFileUploader } from '@/hooks/useFileUploader';
import AddRewardItemDialog from './AddRewardItemDialog';
import React from 'react';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AppDateTimePicker from '@/common/components/form-inputs/AppDateTimePicker';

interface Props {
	type: FormType;
	details?: GameColorGallerySeriesDTORequest;
	setType: Dispatch<SetStateAction<FormType>>;
}

interface RewardItem {
	rewardId: string;
	name: string;
	count: string;
	[key: string]: any;
}

export default function GalleryInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createGallery] = useCreateGalleryMutation();
	const [updateGallery] = useUpdateGalleryMutation();
	const [imageFile, setImageFile] = useState<FileInfo | null>(null);
	const [bgImageFile, setBgImageFile] = useState<FileInfo | null>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef2 = useRef<HTMLInputElement>(null);
	const { uploadFile } = useFileUploader();

	const defaultValues: DefaultValues<GameColorGallerySeriesDTORequest> = {};
	useEffect(() => {
		if (type == FormType.edit) {
			if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
			if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
			if (details?.descriptionZh)
				methods.setValue('descriptionZh', details.descriptionZh);
			if (details?.descriptionEn)
				methods.setValue('descriptionEn', details.descriptionEn);
			if (details?.color) methods.setValue('color', details.color);
			if (details?.publishedStartAt)
				methods.setValue('publishedStartAt', details.publishedStartAt);
			if (details?.publishedEndAt)
				methods.setValue('publishedEndAt', details.publishedEndAt);
			if (details?.rewardId) methods.setValue('rewardId', details.rewardId);
			if (details?.backgroundPic) {
				setBgImageFile(details.backgroundPic);
				methods.setValue('cover', 'file');
			}

			if (details?.completePic) {
				setImageFile(details.completePic);
				methods.setValue('complete', 'file');
			}
			if (details?.rewardId) {
				methods.setValue('rewardList', [
					{
						rewardId: details.rewardId,
						titleZh:
							details?.rewardDetail?.seriesTitleZh +
							' - ' +
							details?.rewardDetail?.titleZh,
					},
				]);

				optionsReplace([
					{
						rewardId: details.rewardId,
						titleZh:
							details?.rewardDetail?.seriesTitleZh +
							' - ' +
							details?.rewardDetail?.titleZh,
					},
				]);
			}
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		publishedStartAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		publishedEndAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		titleZh: Yup.string().required(t('errorMessage.required')),
		color: Yup.string().required(t('errorMessage.pleaseSelect')),
		descriptionZh: Yup.string().required(t('errorMessage.required')),
		cover: Yup.string().required(t('errorMessage.pleaseSelect')),
		complete: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<GameColorGallerySeriesDTORequest>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	const coverField = useController({
		name: 'cover',
		control: methods.control,
	});
	const completeField = useController({
		name: 'complete',
		control: methods.control,
	});

	const {
		fields: rewardListField,
		append: optionsAppend,
		remove: optionsRemove,
		update: optionsUpdate,
		replace: optionsReplace,
	}: any = useFieldArray({
		control: methods.control,
		name: 'rewardList',
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<GameColorGallerySeriesDTORequest> = async (
		data
	) => {
		const obj: GameColorGallerySeriesDTORequest = { ...data, rewardId: '' };
		if (data.rewardList.length > 0) {
			obj.rewardId = data.rewardList[0].rewardId;
		} else {
			delete obj?.rewardId;
		}
		const response =
			type == FormType.create
				? await createGallery({
						...obj,
						backgroundPic: bgImageFile,
						completePic: imageFile,
				  })
				: await updateGallery({
						...obj,
						backgroundPic: bgImageFile,
						completePic: imageFile,
						id: details?.id || '',
				  });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createMeditationSuccess')
					: t('snackbar.editMeditationSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/treasury-manage/gallery-list');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/treasury-manage/gallery-list');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
		type: string
	) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			if (type == 'image') {
				setImageFile(res);
				methods.setValue('complete', res.name);
				methods.trigger('complete');
			}
			if (type == 'bg') {
				setBgImageFile(res);
				methods.setValue('cover', res.name);
				methods.trigger('cover');
			}
		}
	};

	const handleDeleteFile = (type: string) => {
		if (type == 'image') {
			setImageFile(null);
			methods.setValue('complete', '');
			imageInputRef.current && (imageInputRef.current.value = '');
		}
		if (type == 'bg') {
			setBgImageFile(null);
			methods.setValue('cover', '');
			imageInputRef2.current && (imageInputRef2.current.value = '');
		}
	};

	const handleGetRewardList = (list: RewardItem[]) => {
		if (list.length > 0) {
			const arr = methods.getValues('rewardList') || [];
			arr.push(list[0]);
			optionsReplace(arr);
		}
	};

	const handleDeleteReward = (index: number) => {
		optionsRemove(index);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('chartletList.seriesTitle')}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('chartletList.seriesTitle')}
											maxLength={100}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppColorTextField
									control={methods.control}
									name='color'
									label={t('chartletList.seriesColor')}
								/>
							</Grid>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='descriptionZh'
											multiline
											label={t('galleryList.seriesDescription_55')}
											minRows={4}
											maxLength={55}
											showMaxLength
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='descriptionEn'
											multiline
											label={t('galleryList.seriesDescription_55')}
											minRows={4}
											maxLength={55}
											showMaxLength
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedStartAt'
									label={t('chartletList.pushedTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedEndAt'
									label={t('chartletList.pulledTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={12}>
								<Box
									display='flex'
									justifyContent='space-between'
									alignItems='center'
									marginBottom={'10px'}
								>
									<Typography
										variant='body2'
										sx={{
											display: 'flex',
										}}
									>
										<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
										&nbsp;
										{t('chartletList.tips')}
									</Typography>
								</Box>
							</Grid>
							{rewardListField.length < 1 && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('chartletList.collectAllRewards_')}
									</Typography>
									<AddRewardItemDialog
										handleGetRewardList={handleGetRewardList}
									/>
								</Grid>
							)}

							{rewardListField.map((item: any, index: number) => (
								<React.Fragment key={item.rewardId}>
									<Grid item xs={6} sx={{ display: 'flex' }}>
										<AppTextField
											disabled
											control={methods.control}
											name={`rewardList.${index}.titleZh`}
											label={t('chartletList.collectAllRewards_')}
										/>
										<IconButton
											aria-label='close'
											onClick={() => handleDeleteReward(index)}
											color='error'
										>
											<DeleteOutlineOutlinedIcon />
										</IconButton>
									</Grid>
								</React.Fragment>
							))}
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('chartletList.seriesBgImage')}
								</Typography>
								{bgImageFile && (
									<img
										src={bgImageFile?.url || ''}
										width={245}
										height={245}
										style={{
											objectFit: 'contain',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{bgImageFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef2}
												onChange={(event) => {
													handleUpload(event, 'bg');
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef2}
												onChange={(event) => {
													handleUpload(event, 'bg');
												}}
											/>
										</Button>
									)}
									{bgImageFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('bg');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{coverField.fieldState.error?.message}
								</FormHelperText>
							</Grid>
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('galleryList.seriesImage')}
								</Typography>
								{imageFile && (
									<img
										src={imageFile?.url || ''}
										width={245}
										height={245}
										style={{
											objectFit: 'contain',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{imageFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event, 'image');
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event, 'image');
												}}
											/>
										</Button>
									)}
									{imageFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('image');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{completeField.fieldState.error?.message}
								</FormHelperText>
							</Grid>
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.add')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
